import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Navigation } from "lucide-react";
import { NAV_LINKS, BUSINESS } from "../data";

const Logo = () => (
  <a
    href="#home"
    data-testid="nav-logo"
    className="group flex items-center gap-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-neon rounded-md"
  >
    <span className="font-display text-lg sm:text-xl font-black tracking-tighter text-white">
      MEGA<span className="text-neon">WIN</span>5K
    </span>
  </a>
);

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 inset-x-0 z-50 transition-colors duration-300 ${
        scrolled
          ? "bg-black/60 backdrop-blur-2xl border-b border-white/10"
          : "bg-transparent border-b border-transparent"
      }`}
    >
      <nav className="max-w-7xl mx-auto px-6 md:px-12 h-16 md:h-20 flex items-center justify-between">
        <Logo />

        <ul className="hidden lg:flex items-center gap-8">
          {NAV_LINKS.map((l) => (
            <li key={l.href}>
              <a
                href={l.href}
                data-testid={`nav-link-${l.label.toLowerCase()}`}
                className="relative text-sm text-white/70 hover:text-white transition-colors duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-neon rounded-md after:absolute after:left-0 after:-bottom-1 after:h-px after:w-0 after:bg-neon after:transition-[width] after:duration-300 hover:after:w-full"
              >
                {l.label}
              </a>
            </li>
          ))}
        </ul>

        <div className="hidden lg:flex items-center gap-3">
          <a
            href={BUSINESS.directions}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="nav-directions-btn"
            className="group inline-flex items-center gap-2 rounded-full bg-neon text-black font-semibold px-6 py-2.5 text-sm hover:shadow-[0_0_24px_rgba(0,240,255,0.6)] transition-shadow duration-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white"
          >
            <Navigation size={16} />
            Get Directions
          </a>
        </div>

        <button
          data-testid="mobile-menu-toggle"
          aria-label={open ? "Close menu" : "Open menu"}
          aria-expanded={open}
          onClick={() => setOpen((v) => !v)}
          className="lg:hidden inline-flex items-center justify-center w-11 h-11 rounded-full glass text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-neon"
        >
          {open ? <X size={20} /> : <Menu size={20} />}
        </button>
      </nav>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-menu"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
            className="lg:hidden overflow-hidden bg-[#080808] backdrop-blur-2xl border-b border-white/10"
          >
            <ul className="px-6 py-4 flex flex-col gap-1">
              {NAV_LINKS.map((l) => (
                <li key={l.href}>
                  <a
                    href={l.href}
                    data-testid={`mobile-nav-link-${l.label.toLowerCase()}`}
                    onClick={() => setOpen(false)}
                    className="block py-3 text-base text-white/80 hover:text-neon transition-colors duration-200"
                  >
                    {l.label}
                  </a>
                </li>
              ))}
              <li className="pt-3">
                <a
                  href={BUSINESS.directions}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="mobile-nav-directions-btn"
                  onClick={() => setOpen(false)}
                  className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-neon text-black font-semibold px-6 py-3"
                >
                  <Navigation size={16} /> Get Directions
                </a>
              </li>
            </ul>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};
